'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

// Animated checkmark that draws itself
function AnimatedCheckmark({ isVisible, delay }: { isVisible: boolean; delay: number }) {
  return (
    <svg
      className="w-24 h-24 lg:w-32 lg:h-32"
      viewBox="0 0 100 100"
      fill="none"
    >
      <path
        d="M20 50 L40 70 L80 30"
        stroke="url(#checkGradient)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          'transition-all duration-1000',
          isVisible ? 'stroke-dashoffset-0' : ''
        )}
        style={{
          strokeDasharray: 100,
          strokeDashoffset: isVisible ? 0 : 100,
          transitionDelay: `${delay}ms`,
        }}
      />
      <defs>
        <linearGradient id="checkGradient" x1="20" y1="50" x2="80" y2="30">
          <stop offset="0%" stopColor="hsl(271 81% 56%)" />
          <stop offset="100%" stopColor="hsl(290 75% 60%)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Comparison table component
function ComparisonTable({ isVisible }: { isVisible: boolean }) {
  const comparisons = [
    { feature: 'Engagement structure', others: 'Vague networking', purple: 'Scoped deliverables' },
    { feature: 'Payment model', others: 'Free / exposure', purple: 'Escrow-protected' },
    { feature: 'Quality assurance', others: 'None', purple: 'Platform guarantee' },
    { feature: 'Participant vetting', others: 'Self-reported', purple: 'Verified credentials' },
    { feature: 'Outcome focus', others: 'Meetings held', purple: 'Decisions made' },
  ];

  return (
    <div
      className={cn(
        'transition-all duration-700 delay-1000',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
    >
      <div className="overflow-hidden rounded-2xl border border-white/10">
        <table className="w-full">
          <thead>
            <tr className="bg-white/5">
              <th className="text-left py-4 px-6 text-purple-200/70 font-medium text-sm uppercase tracking-wider">
                Feature
              </th>
              <th className="text-center py-4 px-6 text-purple-200/70 font-medium text-sm uppercase tracking-wider">
                Others
              </th>
              <th className="text-center py-4 px-6 text-amber-300/70 font-medium text-sm uppercase tracking-wider">
                Purple
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisons.map((row, index) => (
              <tr
                key={index}
                className={cn(
                  'border-t border-white/5 transition-all',
                  isVisible && 'animate-in fade-in-0'
                )}
                style={{ animationDelay: `${1200 + index * 100}ms` }}
              >
                <td className="py-4 px-6 text-purple-100">{row.feature}</td>
                <td className="py-4 px-6 text-center">
                  <div className="inline-flex items-center gap-2 text-purple-300/60">
                    <X className="w-4 h-4" />
                    <span className="text-sm">{row.others}</span>
                  </div>
                </td>
                <td className="py-4 px-6 text-center">
                  <div className="inline-flex items-center gap-2 text-amber-300">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">{row.purple}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Large animated text reveal
function RevealText({
  children,
  delay,
  className,
  isVisible,
}: {
  children: React.ReactNode;
  delay: number;
  className?: string;
  isVisible: boolean;
}) {
  return (
    <div className="overflow-hidden">
      <div
        className={cn(
          'transition-all duration-1000 ease-out',
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
          className
        )}
        style={{ transitionDelay: `${delay}ms` }}
      >
        {children}
      </div>
    </div>
  );
}

export default function DifferenceSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, hsl(274 87% 12%) 0%, hsl(271 81% 20%) 50%, hsl(280 75% 15%) 100%)',
      }}
    >
      {/* Grain texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Large number watermarks */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span
          className={cn(
            'absolute top-20 left-10 text-[20rem] font-display font-bold text-white/[0.02] leading-none transition-all duration-1000',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          01
        </span>
        <span
          className={cn(
            'absolute top-1/3 right-10 text-[20rem] font-display font-bold text-white/[0.02] leading-none transition-all duration-1000 delay-300',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          02
        </span>
        <span
          className={cn(
            'absolute bottom-20 left-1/4 text-[20rem] font-display font-bold text-white/[0.02] leading-none transition-all duration-1000 delay-500',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          03
        </span>
      </div>

      {/* Gradient orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, hsl(280 80% 50% / 0.2) 0%, transparent 60%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, hsl(271 81% 56% / 0.2) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* Animated checkmark decorations */}
      <div className="absolute top-20 left-10 opacity-20 hidden lg:block">
        <AnimatedCheckmark isVisible={isVisible} delay={500} />
      </div>
      <div className="absolute bottom-20 right-10 opacity-20 hidden lg:block">
        <AnimatedCheckmark isVisible={isVisible} delay={800} />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section label */}
        <div
          className={cn(
            'text-center mb-16 transition-all duration-700',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <p className="text-sm font-semibold text-purple-300 uppercase tracking-[0.3em]">
            The Purple Difference
          </p>
        </div>

        {/* Main manifesto statements */}
        <div className="space-y-6 lg:space-y-8 text-center mb-20">
          <RevealText delay={100} isVisible={isVisible}>
            <h2
              className="font-display font-bold text-white leading-[1.1]"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
            >
              No noise.
            </h2>
          </RevealText>

          <RevealText delay={250} isVisible={isVisible}>
            <h2
              className="font-display font-bold text-purple-200/80 leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}
            >
              No free mentorship theater.
            </h2>
          </RevealText>

          <RevealText delay={400} isVisible={isVisible}>
            <h2
              className="font-display font-bold text-purple-300/60 leading-[1.1]"
              style={{ fontSize: 'clamp(1.8rem, 4.5vw, 3.5rem)' }}
            >
              No vanity community.
            </h2>
          </RevealText>

          <div className="pt-8 lg:pt-12">
            <RevealText delay={650} isVisible={isVisible}>
              <h2
                className="font-serif italic text-amber-300 leading-[1.2]"
                style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)' }}
              >
                Just decisions that matter.
              </h2>
            </RevealText>
          </div>
        </div>

        {/* Divider with animated gradient */}
        <div
          className={cn(
            'mx-auto mb-16 transition-all duration-1000 delay-800',
            isVisible ? 'w-40 opacity-100' : 'w-0 opacity-0'
          )}
        >
          <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent" />
        </div>

        {/* Supporting text */}
        <div
          className={cn(
            'max-w-3xl mx-auto text-center mb-20 transition-all duration-700 delay-900',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <p className="text-xl lg:text-2xl text-purple-100 leading-relaxed mb-6">
            Every interaction is structured. Every outcome is paid. Every signal matters.
          </p>
          <p className="text-lg text-purple-300/70 leading-relaxed">
            Purple Venture Studio is a <span className="text-white font-medium">Structured Startup–Investor Matchmaking platform</span> that converts experience, intelligence, and execution into{' '}
            <span className="text-white font-medium">measurable outcomes</span>.
          </p>
        </div>

        {/* Purple IS NOT / Purple IS sections */}
        <div
          className={cn(
            'max-w-5xl mx-auto mb-20 transition-all duration-700 delay-1000',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Purple IS NOT */}
            <div className="relative">
              <div
                className="relative p-8 rounded-2xl"
                style={{
                  background: 'rgba(220, 38, 38, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                }}
              >
                <h3 className="text-xl font-display font-semibold text-red-300 mb-6 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Purple IS NOT
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-white font-medium mb-1">An accelerator</p>
                    <p className="text-sm text-purple-300/60">No cohorts. No demo days. No equity-for-access model.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">A mentor marketplace</p>
                    <p className="text-sm text-purple-300/60">No vanity networking, inspirational talks, or unpaid advisory.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">A freelancer platform</p>
                    <p className="text-sm text-purple-300/60">No open-ended hourly billing or unaccountable delivery.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Purple IS */}
            <div className="relative">
              <div
                className="relative p-8 rounded-2xl"
                style={{
                  background: 'rgba(34, 197, 94, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                }}
              >
                <h3 className="text-xl font-display font-semibold text-green-300 mb-6 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Purple IS
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-white font-medium mb-1">A structured execution platform</p>
                    <p className="text-sm text-purple-300/60">Helping startups establish, scale, and become investor-ready through disciplined decision support.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">A decision-leverage system</p>
                    <p className="text-sm text-purple-300/60">Enabling thesis building, standardized diligence, and scalable evaluation for investors.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom statement */}
          <div className="text-center mt-12">
            <p className="text-lg text-purple-200 leading-relaxed">
              Purple Venture Studio does not optimize for <span className="text-purple-300/60 line-through">access, visibility, or volume</span>.
              <br />
              It optimizes for <span className="text-amber-300 font-medium">decision quality at the moments that matter most</span>.
            </p>
          </div>
        </div>

        {/* Comparison table */}
        <div className="max-w-3xl mx-auto">
          <ComparisonTable isVisible={isVisible} />
        </div>

        {/* Bottom pillars */}
        <div
          className={cn(
            'grid md:grid-cols-3 gap-6 mt-20 transition-all duration-700 delay-1200',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          {[
            {
              number: '01',
              label: 'Structured',
              description: 'Clear scope, deliverables, and timelines',
            },
            {
              number: '02',
              label: 'Paid',
              description: 'No free labor disguised as opportunity',
            },
            {
              number: '03',
              label: 'Outcome-driven',
              description: 'Success = decisions made, not meetings held',
            },
          ].map((pillar, index) => (
            <div
              key={pillar.label}
              className="relative group"
            >
              <div
                className="relative p-8 rounded-2xl transition-all duration-500 group-hover:-translate-y-2"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {/* Number */}
                <span className="absolute top-4 right-4 text-4xl font-display font-bold text-white/10">
                  {pillar.number}
                </span>

                <h3 className="text-2xl font-display font-semibold text-white mb-3">
                  {pillar.label}
                </h3>
                <p className="text-purple-200/70">{pillar.description}</p>

                {/* Hover gradient line */}
                <div
                  className={cn(
                    'absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-300',
                    'opacity-0 group-hover:opacity-100'
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
